import { useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  IconButton,
  Container
} from '@mui/material';

import FolderIcon from '@mui/icons-material/Folder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useLocation } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import PageTitle from '../../components/PageHeading';
import Page from '../../components/Page';
import Label from '../../components/Label';
import BrudCrumbs from '../../components/BreadCrumbs';

const getLabelColor = (s) =>
  ({
    New: 'info',
    Contacted: 'info',
    Error: 'error',
    Warning: 'warning',
    DND: 'warning',
    Valid: 'success',
    Submitted: 'success'
  }[s] || 'warning');

const getIconColor = (s) =>
  ({
    'No. of Records': 'info',
    'Valid Records': 'success',
    'Invalid Records': 'error',
    'Phone - FPD': 'warning'
  }[s] || 'warning');

export default function CheckBureau() {
  const location = useLocation();
  const { rowId } = location.state || {};

  const summaryCards = useMemo(
    () => [
      {
        icon: <FolderIcon />,
        value: 8,
        label: 'No. of Records'
      },
      {
        icon: <TrendingUpIcon />,
        value: 5,
        label: 'No. of Records Bureau Fetched'
      },
      {
        icon: <WarningAmberIcon />,
        value: 3,
        label: 'Bureau Not Fetched'
      },
      {
        icon: <AccessTimeIcon />,
        value: 0,
        label: 'Phone - FPD'
      }
    ],
    []
  );

  const customers = useMemo(
    () => [
      {
        name: 'Abhinav Sharma',
        mobile: '9805079253',
        cibilScore: 780,
        status: 'Good',
        recommendation: 'Approved',
        bureauStatus: 'Fetched'
      },
      {
        name: 'Anil Kumar Parnami',
        mobile: '9194976544',
        cibilScore: 650,
        status: 'Fair',
        recommendation: 'Review Required',
        bureauStatus: 'Fetched'
      },
      {
        name: 'Jagdish Joshi',
        mobile: '9189688332',
        cibilScore: 820,
        status: 'Excellent',
        recommendation: 'Approved',
        bureauStatus: 'Fetched'
      },
      {
        name: 'Piyush Garodhara',
        mobile: '9876543210',
        cibilScore: 590,
        status: 'Poor',
        recommendation: 'Rejected',
        bureauStatus: 'Fetched'
      },
      {
        name: 'Rahul Verma',
        mobile: '9988776655',
        cibilScore: null,
        status: 'Not Fetched',
        recommendation: 'Pending',
        bureauStatus: 'Not Fetched'
      },
      {
        name: 'Priya Singh',
        mobile: '9123456789',
        cibilScore: null,
        status: 'Not Fetched',
        recommendation: 'Pending',
        bureauStatus: 'Not Fetched'
      },
      {
        name: 'Amit Kumar',
        mobile: '9876501234',
        cibilScore: 720,
        status: 'Good',
        recommendation: 'Approved',
        bureauStatus: 'Fetched'
      },
      {
        name: 'Neha Gupta',
        mobile: '9123987650',
        cibilScore: null,
        status: 'Not Fetched',
        recommendation: 'Pending',
        bureauStatus: 'Not Fetched'
      }
    ],
    []
  );

  return (
    <Page title="Partner Management System || Check Bureau">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Check Bureau" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          {/* -------- SUMMARY CARDS -------- */}
          <Grid container spacing={2} mb={3}>
            {summaryCards?.map((card, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderRadius: 2
                  }}
                >
                  <Label
                    style={{ padding: '20px 10px', borderRadius: '12px' }}
                    variant="ghost"
                    color={getIconColor(card.label)}
                  >
                    {card?.icon}
                  </Label>
                  <Box>
                    <Typography fontSize={22} fontWeight={700}>
                      {card?.value}
                    </Typography>
                    <Typography color="text.secondary"> {card?.label}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* -------- TABLE CARD -------- */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography
              style={{ marginTop: '30px' }}
              variant="body1"
              className="headingLineUp headingLineUpOut"
            >
              Bureau Check Results
            </Typography>

            <IconButton>
              <DownloadIcon />
            </IconButton>
          </Box>
          <Box className="viewLeadCard cardTopMinusMargin" mt={5} mb={3}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f6f9fc' }}>
                    <TableCell>
                      <b>NAME</b>
                    </TableCell>
                    <TableCell>
                      <b>MOBILE</b>
                    </TableCell>
                    <TableCell>
                      <b>CIBIL SCORE</b>
                    </TableCell>
                    <TableCell>
                      <b>STATUS</b>
                    </TableCell>
                    <TableCell>
                      <b>RECOMMENDATION</b>
                    </TableCell>
                    <TableCell>
                      <b>BUREAU STATUS</b>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {customers?.map((r, index) => (
                    <TableRow key={index}>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.mobile}</TableCell>
                      <TableCell>{r.cibilScore}</TableCell>
                      <TableCell>{r.status}</TableCell>
                      <TableCell>{r.recommendation}</TableCell>
                      <TableCell>{r.bureauStatus}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Container>
    </Page>
  );
}
